/**
 * <virtual-try-on> — PULERA fit finder + virtual try-on (v4, 2026-09-21).
 * Markup: snippets/virtual-try-on.liquid. ONE screen: the camera opens with the frame already on the face
 * (3D via virtual-try-on-3d.js, loaded on demand: MediaPipe's face pose matrix drives a textured front + temples
 * behind a depth-only face occluder; the flat PNG overlay below is the fallback), the face is MEASURED while the
 * shopper wears it (a few seconds of steady frames, medianed), and the fit verdict appears under the live view —
 * sold hard only when the numbers back it.
 *
 * Measuring: MediaPipe Face Landmarker (478 landmarks incl. irises). The iris is ~11.7 mm across in
 * nearly everyone, which calibrates the camera; face width is depth-corrected with the landmark z so
 * the temples (further from the lens than the eyes) are not under-read. ≥90 gated frames over ≥3.2 s are medianed.
 * Everything runs on-device; the only thing ever stored is the measurement profile, in localStorage,
 * so the verdict can show on every product page without rescanning.
 */
const MP_VERSION = '0.10.14';
const MP_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MP_VERSION}`;
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const IRIS_MM = 11.7;
const SCAN_SAMPLES = 90; // minimum steady frames
const SCAN_MIN_MS = 3200; // minimum time holding still (phones run 20–30 fps, so ~3–4.5 s)
const SCAN_MAX_MS = 8000; // stop waiting for a steadier read after this
const SCAN_SPREAD_MM = 4; // inter-quartile spread of the face-width samples that counts as steady
const RESCAN_BLEND_MM = 5; // a rescan within this of the saved read is the same face: average them so repeat scans converge
const STORE_KEY = 'pulera_fit_v1';
const FA_BASE = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2';
const FA_WEIGHTS = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights'; // npm package ships no weights
const GENDER_MIN_CONFIDENCE = 0.85;
const DEFAULTS = {
  enabled: true, button_label: 'AI Try On', express_checkout: true, show_badge: true, gender_detect: true, tolerance: 0,
  headline_great: 'These were made for you.', headline_good: 'A strong fit — one thing to know.',
  headline_fair: 'Wearable — but not your best PULERA.', headline_poor: 'Honestly? Not this one.',
  closer_neutral: 'If you have been on the fence about {title}, this is the one.',
  closer_feminine: 'This is the pair that finishes an outfit — {title} was made for exactly your face.',
  closer_masculine: 'This is the pair that makes a plain tee look deliberate — {title} sits on you like it was cut for you.',
  cta_label: 'Add to bag & check out · {price}', privacy_note: 'Runs entirely on your device. Nothing is uploaded, ever.',
};
const LM = {
  eyeL: 33, eyeR: 263, faceL: 234, faceR: 454, nose: 1, chin: 152, top: 10, jawL: 132, jawR: 361,
  foreL: 103, foreR: 332, alarL: 48, alarR: 278, irisL: 468, irisR: 473, irisLa: 469, irisLb: 471, irisRa: 474, irisRb: 476,
};

const $ = (root, ref) => root.querySelector(`[ref="${ref}"]`);
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const lerp = (a, b, t) => a + (b - a) * t;
const median = (arr) => { const s = [...arr].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const iqr = (arr) => { const s = [...arr].sort((a, b) => a - b); return s.length < 4 ? 0 : s[Math.floor(s.length * 0.75)] - s[Math.floor(s.length * 0.25)]; };
const r0 = (v) => Math.round(v);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Real-world face measurements from one frame of landmarks. Returns null when the irises are not usable. */
export function measureFace(lm, W, H) {
  const P = (i) => ({ x: lm[i].x * W, y: lm[i].y * H, z: (lm[i].z || 0) * W });
  const irisPx = (dist(P(LM.irisLa), P(LM.irisLb)) + dist(P(LM.irisRa), P(LM.irisRb))) / 2;
  if (!(irisPx > 4)) return null;
  const mmPerPx = IRIS_MM / irisPx;
  const Ze = 0.85 * Math.max(W, H) * mmPerPx; // camera→eye-plane distance (mm): ~60° FOV spans the sensor's LONG side whichever way the frame is rotated
  const zEye = (P(LM.irisL).z + P(LM.irisR).z) / 2;
  const corr = (p) => clamp(1 + ((p.z - zEye) * mmPerPx) / Ze, 0.9, 1.2);
  const mm = (a, b) => dist(a, b) * mmPerPx * (corr(a) + corr(b)) / 2;
  const eL = P(LM.eyeL), eR = P(LM.eyeR), fL = P(LM.faceL), fR = P(LM.faceR), nose = P(LM.nose);
  const cheek = dist(fL, fR);
  return {
    faceW: mm(fL, fR),
    pd: dist(P(LM.irisL), P(LM.irisR)) * mmPerPx + 2.5, // near PD → distance PD
    noseW: mm(P(LM.alarL), P(LM.alarR)),
    len: dist(P(LM.top), P(LM.chin)) / cheek,
    jaw: dist(P(LM.jawL), P(LM.jawR)) / cheek,
    fore: dist(P(LM.foreL), P(LM.foreR)) / cheek,
    yaw: (nose.x - (eL.x + eR.x) / 2) / dist(eL, eR),
    roll: (Math.atan2(eR.y - eL.y, eR.x - eL.x) * 180) / Math.PI,
    // against the SHORT side: a desktop webcam frames landscape (1280x720), where face/width never
    // reaches a phone-portrait threshold and the scan sat on "come closer" forever
    size: cheek / Math.min(W, H),
    irisPx,
  };
}

/** Face-shape read from mesh ratios (length/width, jaw/cheek, forehead/cheek). A styling heuristic, not anatomy. */
export function classifyShape(m) {
  const { len, jaw, fore } = m;
  if (len > 1.14) return 'oblong';
  if (jaw > 0.9 && fore > 0.92 && len < 1.06) return 'square';
  if (fore > 0.95 && jaw < 0.8) return 'heart';
  if (len < 1.0 && jaw > 0.8 && jaw < 0.9) return 'round';
  return 'oval';
}

const SHAPE_SCORE = {
  round: { square: 100, 'cat-eye': 90, oval: 60, round: 40 },
  square: { round: 100, oval: 95, 'cat-eye': 85, square: 55 },
  oval: { square: 95, 'cat-eye': 95, oval: 85, round: 85 },
  heart: { 'cat-eye': 100, oval: 90, round: 85, square: 70 },
  oblong: { square: 90, oval: 85, 'cat-eye': 80, round: 70 },
};
const SHAPE_COPY = {
  round: { square: 'A square frame is the classic move for a rounder face — the straight lines add definition.', 'cat-eye': 'The lifted corners of a cat-eye sharpen a softer face.', oval: 'An oval frame echoes a round face rather than contrasting it — it can read a little soft.', round: 'Round on round tends to flatten the face; a squarer frame would do more for you.' },
  square: { round: 'Round lenses soften a strong jaw — the contrast is what makes it work.', oval: 'An oval frame takes the edge off a square jaw without hiding it.', 'cat-eye': 'A cat-eye lifts the face and softens the jawline.', square: 'Square on square doubles down on the angles — it can look severe.' },
  oval: { square: 'An oval face carries a square frame effortlessly — the angles give it attitude.', 'cat-eye': 'A cat-eye plays up the cheekbones on an oval face.', oval: 'Oval on oval is easy and balanced.', round: 'Round lenses sit naturally on an oval face.' },
  heart: { 'cat-eye': 'A cat-eye mirrors the lift of a heart-shaped face — it is the textbook match.', oval: 'An oval frame balances a wider forehead against a narrower chin.', round: 'Round lenses add width lower on the face, which balances a heart shape.', square: 'A heavy square top can over-widen the forehead on a heart-shaped face.' },
  oblong: { square: 'A deep square frame breaks up a longer face — it visibly shortens it.', oval: 'An oval frame adds width where a longer face wants it.', 'cat-eye': 'A cat-eye adds width and lift to a longer face.', round: 'Round lenses add some width, but a taller frame would balance a long face better.' },
};

/** Score one frame against a measurement profile. Returns null when the product has no specs. */
export function fitReport(m, specs, shape, tolerance = 0) {
  if (!m || !specs || !specs.width || !specs.lens || !specs.bridge) return null;
  const tol = clamp(Number(tolerance) || 0, 0, 6);
  // Bands follow real-world dispensing tolerances (acetate flexes, ±4 mm front width is a normal fit
  // window): a genuinely wearable frame lands 85+, and only a truly wrong one drops out of the 80s.
  // Piecewise-LINEAR between the anchors, not steps — the scan reads a face to about ±3 mm, and a
  // step curve turns that wobble into 10-point score jumps between two scans of the same face.
  const ramp = (x, stops) => {
    if (x <= stops[0][0]) return 100;
    for (let i = 1; i < stops.length; i++) {
      const [x0, y0] = stops[i - 1], [x1, y1] = stops[i];
      if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
    return stops[stops.length - 1][1];
  };
  const dW = specs.width - m.faceW, aW = Math.max(0, Math.abs(dW) - tol);
  const widthScore = ramp(aW, [[4, 100], [7, 90], [10, 75], [14, 55], [18, 30]]);
  // Lens-centre spacing (A + DBL) normally runs ~5 mm wider than the wearer's PD; score the deviation from that.
  const dPD = specs.lens + specs.bridge - m.pd, aPD = Math.abs(dPD - 5);
  const pdScore = ramp(aPD, [[4, 100], [8, 85], [11, 65], [15, 45]]);
  const dB = specs.bridge - m.noseW * 0.6, aB = Math.abs(dB);
  const bridgeScore = ramp(aB, [[3, 100], [6, 85], [10, 65]]);
  const frameShape = specs.shape || 'square';
  const shapeScore = Math.max(55, (SHAPE_SCORE[shape] || SHAPE_SCORE.oval)[frameShape] ?? 80);
  let score = Math.round(0.40 * widthScore + 0.25 * pdScore + 0.20 * bridgeScore + 0.15 * shapeScore);
  // A frame is only "made for you" when nothing is actually wrong, and never approved when the width is wrong.
  if (widthScore < 60) score = Math.min(score, 69);
  else if (widthScore < 80) score = Math.min(score, 84);
  if ([pdScore, bridgeScore, shapeScore].some((x) => x < 55)) score = Math.min(score, 84);
  const state = (s) => (s >= 85 ? 'good' : s >= 65 ? 'ok' : 'bad');
  const wide = dW > 0;
  const points = [
    { key: 'width', state: state(widthScore), label: 'Width', text:
      aW <= 4 ? `${specs.width} mm front on your ${r0(m.faceW)} mm face — sits flush at the temples, no pinch, no overhang.`
      : aW <= 7 ? (wide ? `Runs ${r0(aW)} mm wide on you — a relaxed, slightly oversized sit.` : `Runs ${r0(aW)} mm snug — a close, tailored sit.`)
      : aW <= 10 ? (wide ? `Sits ${r0(aW)} mm wider than your face — it will read oversized.` : `Sits ${r0(aW)} mm narrower than your face — the temples will press in.`)
      : (wide ? `${r0(aW)} mm too wide for you — it will slide and look borrowed.` : `${r0(aW)} mm too narrow for you — it will pinch at the temples.`) },
    { key: 'eyes', state: state(pdScore), label: 'Eyes', text:
      aPD <= 4 ? 'Your eyes sit exactly where they should in the lenses — the look reads intentional, not borrowed.'
      : aPD <= 8 ? `Your eyes sit slightly ${dPD > 5 ? 'inward' : 'outward'} in the lenses — barely noticeable.`
      : `Your eyes sit well ${dPD > 5 ? 'inward' : 'outward'} of centre in the lenses — the frame will look off-balance.` },
    { key: 'bridge', state: state(bridgeScore), label: 'Bridge', text:
      aB <= 3 ? `The ${specs.bridge} mm bridge matches your nose — it will sit where it should.`
      : aB <= 6 ? (dB > 0 ? 'The bridge runs a touch wide for your nose — expect it to sit slightly low.' : 'The bridge runs a touch narrow — it may perch a little high.')
      : (dB > 0 ? 'The bridge is wide for your nose — it will slide down.' : 'The bridge is narrow for your nose — it will sit high and pinch.') },
    { key: 'style', state: state(shapeScore), label: 'Style', text: (SHAPE_COPY[shape] || SHAPE_COPY.oval)[frameShape] || '' },
  ];
  const tier = score >= 85 ? 'great' : score >= 70 ? 'good' : score >= 55 ? 'fair' : 'poor';
  // Signed deviation from ideal, in mm, for the scale bar. Style has no millimetres: it is a
  // contrast axis, negative when the frame contrasts the face shape and positive when it echoes it.
  const echo = ECHO[shape] === frameShape;
  const styleOff = shapeScore >= 90 ? 0 : (echo ? 1 : -1) * Math.min(8, (100 - shapeScore) / 8);
  const W = { width: 40, eyes: 25, bridge: 20, style: 15 };
  const sub = { width: widthScore, eyes: pdScore, bridge: bridgeScore, style: shapeScore };
  const offs = { width: dW, eyes: dPD - 5, bridge: dB, style: styleOff };
  for (const pt of points) {
    pt.offset = offs[pt.key];
    pt.weight = W[pt.key];
    pt.pts = Math.round(W[pt.key] * sub[pt.key] / 100);
    pt.short = pt.key === 'style'
      ? (shapeScore >= 90 ? 'Balanced' : echo ? 'Soft' : 'Bold')
      : Math.abs(pt.offset) < 1 ? 'Ideal'
      : `${pt.offset > 0 ? '+' : '\u2212'}${Math.round(Math.abs(pt.offset))} mm`;
  }
  return { score, tier, approved: tier === 'great' || tier === 'good', points, dW, dPD, dB, widthScore, pdScore, bridgeScore, shapeScore };
}

/** Headline + pitch. Persuasive only when the report earned it; honest (with the better pick) when it did not. */
export function verdictCopy(report, product, alt, cfg = DEFAULTS, voice = 'neutral', frameShape = 'square') {
  const c = { ...DEFAULTS, ...(cfg || {}) };
  const p = report.points;
  const by = (k) => p.find((x) => x.key === k);
  const rank = (x) => ({ good: 2, ok: 1, bad: 0 }[x.state]);
  const flourish = voice !== 'neutral' && report.approved ? (VOICE_STYLE[voice]?.[frameShape] || '') : '';
  const closer = fill(voice === 'feminine' ? c.closer_feminine : voice === 'masculine' ? c.closer_masculine : c.closer_neutral, { title: product.title });
  if (report.tier === 'great') {
    return { headline: c.headline_great, pitch: `The numbers back it: ${by('width').text} ${by('eyes').text} ${flourish || by('style').text} ${closer}`.replace(/\s+/g, ' ').trim() };
  }
  if (report.tier === 'good') {
    const weakest = [...p].sort((a, b) => rank(a) - rank(b))[0];
    return { headline: c.headline_good, pitch: `${by('width').state === 'good' ? by('width').text + ' ' : ''}${flourish || by('style').text} ${weakest.state !== 'good' ? weakest.text : ''} ${closer}`.replace(/\s+/g, ' ').trim() };
  }
  const worst = [...p].sort((a, b) => rank(a) - rank(b))[0];
  const altLine = alt ? ` Your fit in the line is ${alt.title} — ${alt.report.points[0].text}` : '';
  if (report.tier === 'fair') return { headline: c.headline_fair, pitch: `Here is the honest read: ${worst.text}${altLine}` };
  return { headline: c.headline_poor, pitch: `${worst.text}${altLine}` };
}

/** Tone-of-voice flourishes. Only ever change wording; never name a gender to the shopper. */
const VOICE_STYLE = {
  feminine: {
    square: 'A bold square frame reads as confidence — editorial, not borrowed.',
    'cat-eye': 'The lift of a cat-eye does what good eyeliner does — it opens the eyes and lifts the cheekbones.',
    oval: 'A soft oval keeps your face the hero — quietly elegant.',
    round: 'Round lenses read playful and a little retro on you.',
  },
  masculine: {
    square: 'A square frame squares the jaw and reads decisive — the classic silhouette for a reason.',
    'cat-eye': 'A cat-eye worn with intent reads editorial and sharp — a fashion move, not a safe one.',
    oval: 'An oval softens without looking soft — understated and tailored.',
    round: 'Round lenses add a thoughtful, architect-ish edge.',
  },
};
const fill = (tpl, vars) => String(tpl || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ''));

const ECHO = { round: 'round', square: 'square', oval: 'oval', heart: 'cat-eye', oblong: 'rectangle' };
const SHAPE_LABEL = { round: 'Round', square: 'Square', oval: 'Oval', heart: 'Heart', oblong: 'Long' };

/* ---------- fit flow (design 2a): metric vocabulary, swatches ---------- */
// Lucide-style line icons, 24-box paths.
const METRICS = [
  { key: 'width',  K: 'WIDTH',  LO: 'SNUG',     HI: 'LOOSE',
    icon: '<path d="M3 12h18"/><path d="M6.5 8.5 3 12l3.5 3.5"/><path d="M17.5 8.5 21 12l-3.5 3.5"/>' },
  { key: 'eyes',   K: 'EYES',   LO: 'INWARD',   HI: 'OUTWARD',
    icon: '<path d="M2.5 12S6 7.2 12 7.2 21.5 12 21.5 12 18 16.8 12 16.8 2.5 12 2.5 12Z"/><circle cx="12" cy="12" r="2.4"/>' },
  { key: 'bridge', K: 'BRIDGE', LO: 'TIGHT',    HI: 'LOOSE',
    icon: '<circle cx="6.4" cy="14" r="3.4"/><circle cx="17.6" cy="14" r="3.4"/><path d="M9.8 13c1.4-1.1 3-1.1 4.4 0"/><path d="M3 12 4.6 9.4"/><path d="m21 12-1.6-2.6"/>' },
  { key: 'style',  K: 'STYLE',  LO: 'CONTRAST', HI: 'ECHO',
    icon: '<path d="M12 3.2c4 0 6.9 2.9 6.9 6.8 0 5-2.9 10.8-6.9 10.8S5.1 15 5.1 10C5.1 6.1 8 3.2 12 3.2Z"/>' },
];
const MEASURE_KEYS = [['FACE WIDTH', 'faceW', ' mm'], ['PUPIL DISTANCE', 'pd', ' mm'], ['BRIDGE', 'noseW', ' mm'], ['FACE SHAPE', 'shape', '']];
/** Marker sits at 50% for a perfect fit and walks 6% per millimetre of deviation. */
const markerPos = (offset) => `${clamp(50 + (Number(offset) || 0) * 6, 6, 94)}%`;
const VERDICT = (n) => (n >= 90 ? 'Made for you.' : n >= 85 ? 'A great fit.' : n >= 70 ? 'A good fit.' : 'Not your best fit.');
const RING_C = 188.5; // 2πr at r=30

// Split-disc colourway swatches: variant titles read "FRAME/LENS" (or one word for both).
const SWATCH = [
  ['tortoise', 'linear-gradient(135deg,#6B4423 0%,#A9691F 45%,#D9A441 100%)'], ['caramel', '#C8891F'],
  ['clear', '#E6E6EA'], ['crystal', '#E6E6EA'], ['black', '#141414'], ['brown', '#5A3A22'],
  ['blue', '#2B4A9B'], ['red', '#B3261E'], ['orange', '#D2691E'], ['yellow', '#E3B505'],
  ['green', '#2E6B3F'], ['grey', '#8A8A8E'], ['gray', '#8A8A8E'], ['gold', '#C9A227'],
  ['silver', '#C2C2C6'], ['pink', '#D98BA6'], ['purple', '#6B4FA0'], ['white', '#FAFAFA'],
];
const swatchColor = (word) => { const w = String(word || '').toLowerCase(); for (const [k, v] of SWATCH) if (w.includes(k)) return v; return ''; };
const swatchDisc = (title) => {
  const parts = String(title || '').split(/[\/|·]/).map((t) => t.trim()).filter(Boolean);
  const a = swatchColor(parts[0]), b = swatchColor(parts[1] !== undefined ? parts[1] : parts[0]);
  if (!a && !b) return '';
  return `<i class="pf__disc"><s style="background:${a || b}"></s><s style="background:${b || a}"></s></i>`;
};

const Base = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class VirtualTryOn extends Base {
  #variants = []; #catalogue = []; #specs = null; #product = null;
  #current = null; #image = null; #landmarker = null; #stream = null; #raf = 0;
  #mode = 'idle'; // idle | scan | video | photo
  #photo = null; #smooth = null; #lastTs = -1;
  #samples = []; #scanT0 = 0; #scanning = false; #badRun = 0; #noticeUntil = 0; #debug = false;
  #lmReady = null; #lmMode = ''; #gen = 0; #threeState = 'idle'; #lastVt = -1; #onVis = null; #detTick = false; #lastRes = null;
  #profile = null; #report = null; #alt = null; #cfg = DEFAULTS; #faceapi = null; #prevScan = null;
  #tryon3d = null; #tryon3dReady = null; #use3d = false; #photoRes = null; #tune = {};
  #scr = 'scan'; #metric = 0; #other = null; #alts = []; #lastPct = -1; #sweepRaf = 0; #sweepT0 = 0; #ovalH = 290;

  connectedCallback() {
    const parse = (k, d) => { try { return JSON.parse(this.dataset[k] || d); } catch { return JSON.parse(d); } };
    this.#variants = parse('variants', '[]');
    this.#catalogue = parse('catalogue', '[]');
    this.#specs = parse('specs', 'null');
    this.#product = parse('product', '{}');
    this.#cfg = { ...DEFAULTS, ...parse('config', '{}') };
    this.#tune = parse('tune', '{}'); // merchant calibration from the theme editor
    this.#debug = /[?&]tryon_debug=1/.test(location.search); // surfaces caught errors in the status line
    if (this.#cfg.enabled === false || (!this.#variants.length && !this.#specs)) { this.hidden = true; return; }
    this.dialog = $(this, 'dialog'); this.frame = $(this, 'frame');
    this.video = $(this, 'video'); this.canvas = $(this, 'canvas'); this.ctx = this.canvas.getContext('2d');
    $(this, 'open').addEventListener('click', () => this.open());
    $(this, 'close').addEventListener('click', () => this.close());
    this.dialog.addEventListener('close', () => this.#teardown());
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(() => { if (this.dialog.open) this.#resizeStage(); }).observe(this.frame);
    // focus moving into the translateX(100%) details panel scrolls the overflow:hidden frame sideways
    // and it never scrolls back — every screen ends up shifted. Keep the frame pinned at 0,0.
    this.frame.addEventListener('scroll', () => { this.frame.scrollLeft = 0; this.frame.scrollTop = 0; }, { passive: true });
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    $(this, 'file').addEventListener('change', (e) => this.#usePhoto(e.target.files?.[0]));
    this.addEventListener('click', (e) => {
      const a = e.target.closest('[data-action]'); if (!a) return;
      const act = a.dataset.action;
      if (act === 'checkout') this.#checkout();
      else if (act === 'rescan') this.#rescan();
      else if (act === 'photo') $(this, 'file').click();
      else if (act === 'verdict') { if (!this.dialog.open) this.open(); }
      else if (act === 'metric') this.#pickMetric(Number(a.dataset.i));
      else if (act === 'alt') this.#pickAlt(Number(a.dataset.i));
      else if (act === 'details') this.#screen('details');
      else if (act === 'back') this.#screen('results');
      else if (act === 'sheetview') this.#toggleSheet();
    });
    this.#buildChips();
    this.#profile = this.#loadProfile();
    this.#renderBadge();
    if (!window.HTMLDialogElement) this.hidden = true;
  }
  disconnectedCallback() { this.#tryon3d?.dispose(); this.#tryon3d = null; this.#tryon3dReady = null; }

  /* ---------- persistence + badge ---------- */
  #loadProfile() { try { const p = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); return p && p.faceW ? p : null; } catch { return null; } }
  #saveProfile(p) { try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch {} }
  #forget() { this.#profile = null; this.#report = null; try { localStorage.removeItem(STORE_KEY); } catch {} this.#renderBadge(); this.#tryon3d?.setFrame({ faceWidthMm: 0 }); }

  #renderBadge() {
    const badge = $(this, 'badge'); if (!badge) return;
    const hide = () => { badge.hidden = true; badge.replaceChildren(); };
    if (!this.#profile || !this.#specs || this.#cfg.show_badge === false) { hide(); return; }
    const rep = fitReport(this.#profile, this.#specs, this.#profile.shape, this.#cfg.tolerance);
    if (!rep) { hide(); return; }
    const word = { great: 'Great match', good: 'Good fit', fair: 'Fair fit', poor: 'Not your fit' }[rep.tier];
    badge.innerHTML = `<span class="pl-tryon__badge-dot pl-tryon__badge-dot--${rep.tier}"></span>YOUR FIT · ${esc(word).toUpperCase()} · ${rep.score}/100 <button type="button" class="pl-tryon__ghost" data-action="verdict">See why</button>`;
    badge.hidden = false;
  }

  /* ---------- open / close ---------- */
  async open() {
    this.dialog.showModal(); document.documentElement.classList.add('pl-tryon-open');
    try { this.frame.focus({ preventScroll: true }); } catch {} // showModal focuses the × otherwise, drawing a stray focus ring
    this.#select(this.#currentVariantId());
    this.#other = null; this.#metric = 0; this.frame.dataset.collapsed = '1'; // results open on the full-face view; the FIT DETAILS pill raises the sheet
    this.#screen(this.#profile ? 'results' : 'scan'); // a saved scan scores straight away; otherwise measure first
    await this.#startLive();
  }
  close() { if (this.dialog.open) this.dialog.close(); }
  #notice(text, ms) { this.#setStatus(text); this.#noticeUntil = performance.now() + ms; }
  #fail(where, err) { console.warn(`[virtual-try-on] ${where} failed`, err); if (this.#debug) this.#notice(`${where}: ${err?.message || err}`, 8000); }

  #ensureLandmarker() {
    if (this.#landmarker) return Promise.resolve(true);
    if (this.#lmReady) return this.#lmReady; // one download, however many paths ask for it
    this.#setStatus('Loading face tracking…');
    this.#lmReady = (async () => {
      const { FilesetResolver, FaceLandmarker } = await import(`${MP_BASE}/vision_bundle.mjs`);
      const fileset = await FilesetResolver.forVisionTasks(`${MP_BASE}/wasm`);
      this.#landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' }, runningMode: 'VIDEO', numFaces: 1, outputFacialTransformationMatrixes: true,
      });
      this.#lmMode = 'VIDEO';
      return true;
    })().catch((err) => {
      this.#lmReady = null;
      this.#setStatus('Face tracking could not load. Check your connection and try again.'); this.#fail('face tracking', err);
      return false;
    });
    return this.#lmReady;
  }
  /** VIDEO ↔ IMAGE switch; the live loop only detects while the mode is VIDEO, so a switch in flight never throws. */
  async #setRunningMode(mode) {
    if (this.#lmMode === mode) return;
    this.#lmMode = '';
    await this.#landmarker.setOptions({ runningMode: mode });
    this.#lmMode = mode;
  }

  async #ensureCamera() {
    if (this.#stream?.getVideoTracks()[0]?.readyState === 'live') return true;
    this.#stopStream();
    if (!navigator.mediaDevices?.getUserMedia) { this.#setStatus('No camera available — use a photo instead.'); return false; }
    this.#setStatus('Allow camera access to continue');
    try {
      this.#stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
    } catch (err) { this.#setStatus('Camera blocked — use a photo instead.'); this.#fail('camera', err); return false; }
    if (!this.dialog.open) { this.#stopStream(); return false; }
    const stream = this.#stream, track = stream.getVideoTracks()[0];
    // a call, a lock screen or another app can take the camera: come back live instead of freezing on the last frame
    track.addEventListener('ended', () => { if (this.#stream !== stream) return; this.#stopStream(); if (this.dialog.open && this.#mode === 'video') this.#startLive(); });
    if (!this.#onVis) document.addEventListener('visibilitychange', this.#onVis = () => { if (!document.hidden && this.#stream && this.dialog.open) this.video.play().catch(() => {}); });
    this.video.srcObject = stream;
    const played = await this.video.play().then(() => true, () => false);
    if (!played) { // iOS Low Power Mode refuses programmatic playback until a tap
      this.#setStatus('Tap to start the camera');
      await new Promise((r) => this.dialog.addEventListener('pointerdown', r, { once: true }));
      if (!this.dialog.open) { this.#stopStream(); return false; }
      await this.video.play().catch(() => {});
    }
    return true;
  }

  /* ---------- live view: camera + frame on the face; the face is measured in the background ---------- */
  async #startLive() {
    const gen = ++this.#gen;
    this.#photo = null; this.#photoRes = null; this.#setObjectPosition('50% 50%');
    const lm = this.#ensureLandmarker(); // starts the download now; the camera prompt does not wait for it
    if (!this.#tryon3d) this.#ensure3d().then((r) => r && this.#redrawStill()); // 3D takes over when ready
    if (!(await this.#ensureCamera()) || gen !== this.#gen) return;
    this.#mode = 'video'; this.#smooth = null; this.#lastVt = -1;
    this.#loop(); // mirrored preview immediately, glasses + measuring once the model is in
    if (!(await lm) || gen !== this.#gen || this.#mode !== 'video') return;
    await this.#setRunningMode('VIDEO');
    if (gen !== this.#gen || this.#mode !== 'video') return;
    this.#setStatus('');
    if (this.#profile) this.#screen('results'); else this.#beginScan();
  }
  #beginScan() {
    this.#samples = []; this.#scanT0 = 0; this.#badRun = 0; this.#scanning = true; this.frame.dataset.scanning = '1';
    this.#screen('scan'); this.#lastPct = -1; this.#scanUI(0, ''); this.#startSweep();
    if (this.#mode !== 'video') this.#startLive(); // rescan from the photo path needs the camera back
  }
  #rescan() { this.#other = null; this.#prevScan = this.#profile; this.#forget(); this.#beginScan(); }

  #loop = () => {
    if (this.#mode !== 'video' || !this.dialog.open) return;
    const v = this.video;
    if (v.readyState >= 2 && v.videoWidth && v.currentTime !== this.#lastVt) { // one pass per camera frame, not per display tick
      this.#lastVt = v.currentTime;
      if (this.canvas.width !== v.videoWidth || this.canvas.height !== v.videoHeight) { this.canvas.width = v.videoWidth; this.canvas.height = v.videoHeight; }
      const ts = performance.now();
      if (ts > this.#lastTs) {
        this.#lastTs = ts;
        let res = null;
        // Face detection is the heavy step. While MEASURING it runs on every camera frame; on the
        // results screen every other frame is plenty — the 3D pose holds and lerps between hits.
        this.#detTick = !this.#detTick;
        const needDet = this.#scanning || this.#detTick;
        if (needDet && this.#landmarker && this.#lmMode === 'VIDEO') { try { res = this.#landmarker.detectForVideo(v, ts); this.#lastRes = res; } catch (err) { this.#fail('tracking', err); } }
        else res = this.#lastRes; // skipped frame: draw with the last pose so the flat overlay never blinks
        this.#draw(v, res, true);
        if (this.#scanning && this.#lmMode === 'VIDEO') this.#scanFrame(v, res);
      }
    }
    this.#raf = requestAnimationFrame(this.#loop);
  };

  /** One measuring pass over a live frame that #draw has already painted: gate it, keep it, show progress. */
  #scanFrame(v, res) {
    const lm = res?.faceLandmarks?.[0];
    const { ctx, canvas } = this; const W = canvas.width, H = canvas.height;
    const m = lm ? measureFace(lm, W, H) : null;
    let msg = 'Centre your face in the frame', good = false;
    const k = this.#samples.length ? 1.6 : 1; // hysteresis: once locked, the bands widen so a handheld phone does not flicker in and out
    if (m) {
      // the iris is the real quality signal: when it is already well-resolved, distance is fine whatever the framing
      if (m.size < 0.24 / k && m.irisPx < 14) msg = 'Come a little closer';
      else if (Math.abs(m.yaw) > 0.05 * k) msg = 'Look straight at the camera';
      else if (Math.abs(m.roll) > 5 * k) msg = 'Level your head';
      else if (m.irisPx < 9 / k) msg = 'Come closer — more light helps';
      else good = true;
    }
    if (good) { if (!this.#samples.length) this.#scanT0 = performance.now(); this.#samples.push(m); this.#badRun = 0; }
    else if (this.#samples.length && ++this.#badRun > 3) this.#samples.length = Math.max(0, this.#samples.length - 2); // sustained movement costs progress
    const n = this.#samples.length, elapsed = n ? performance.now() - this.#scanT0 : 0;
    const prog = Math.min(1, elapsed / SCAN_MIN_MS, n / SCAN_SAMPLES);
    if (good) msg = prog < 0.34 ? 'Hold still\u2026' : prog < 0.67 ? 'Measuring eye spacing\u2026' : 'Reading face shape\u2026';
    this.#scanUI(Math.min(prog, 0.99), msg);
    if (prog >= 1) {
      // done only once the read is steady (or we have waited long enough)
      const steady = iqr(this.#samples.slice(-SCAN_SAMPLES).map((x) => x.faceW)) <= SCAN_SPREAD_MM;
      if (steady || elapsed >= SCAN_MAX_MS) { this.#scanUI(1, 'Got it'); this.#finishScan(v); }
      else this.#scanUI(0.99, 'Almost there \u2014 hold still');
    }
  }

  #finishScan(source) {
    this.#scanning = false; this.frame.dataset.scanning = '';
    const s = this.#samples.slice(-SCAN_SAMPLES);
    const med = (k) => median(s.map((x) => x[k]));
    const m = { faceW: med('faceW'), pd: med('pd'), noseW: med('noseW'), len: med('len'), jaw: med('jaw'), fore: med('fore'), at: Date.now() };
    // A rescan that reads close to the previous scan is the same face measured twice: average the two
    // so repeat scans converge on one number instead of wobbling the score with each pass.
    const prev = this.#prevScan; this.#prevScan = null;
    if (prev && Number.isFinite(prev.faceW) && Math.abs(prev.faceW - m.faceW) <= RESCAN_BLEND_MM) {
      for (const k of ['faceW', 'pd', 'noseW', 'len', 'jaw', 'fore']) if (Number.isFinite(prev[k])) m[k] = (prev[k] + m[k]) / 2;
    }
    m.shape = classifyShape(m);
    this.#profile = m; this.#saveProfile(m); this.#renderBadge(); this.#sync3dFrame();
    this.#scanUI(1, 'Got it'); this.#stopSweep();
    setTimeout(() => { if (this.dialog.open) { this.frame.dataset.collapsed = '1'; this.#screen('results'); $(this, 'sheet').scrollTop = 0; this.#setStatus(''); } }, 550);
    // tone detection wants a frozen copy of the frame
    const c = document.createElement('canvas'); c.width = this.canvas.width; c.height = this.canvas.height;
    c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
    this.#detectVoice(c);
  }

  async #usePhoto(file) {
    if (!file) return;
    const gen = ++this.#gen; // a live start still in flight must not take the stage back
    this.#stopStream(); this.#mode = 'idle'; this.#scanning = false; this.frame.dataset.scanning = ''; cancelAnimationFrame(this.#raf);
    if (!(await this.#ensureLandmarker()) || gen !== this.#gen) return;
    const bmp = await createImageBitmap(file).catch(() => null);
    if (!bmp) { this.#setStatus('That image could not be read.'); return; }
    const max = 1600, sc = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas'); c.width = Math.round(bmp.width * sc); c.height = Math.round(bmp.height * sc);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    await this.#setRunningMode('IMAGE');
    if (gen !== this.#gen) return;
    const res = this.#landmarker.detect(c), lm = res.faceLandmarks?.[0];
    const m = lm ? measureFace(lm, c.width, c.height) : null;
    if (!m) { this.#setStatus('No face found — try a straight-on photo with both eyes visible.'); return; }
    m.shape = classifyShape(m); m.at = Date.now(); m.fromPhoto = true;
    this.#profile = m; this.#saveProfile(m); this.#renderBadge(); this.#sync3dFrame();
    this.#photo = c; this.#photoRes = res; this.#scanning = false; this.frame.dataset.scanning = '';
    this.#mode = 'photo';
    if (!this.#tryon3d) this.#ensure3d().then((r) => r && this.#redrawStill());
    this.#renderPhoto();
    this.#setStatus(''); this.frame.dataset.collapsed = '1'; this.#screen('results'); $(this, 'sheet').scrollTop = 0;
    this.#detectVoice(c);
  }

  /* ---------- tone of voice (gender-adaptive copy) ---------- */
  #setVoice(voice, manual = false) {
    if (!this.#profile) return;
    this.#profile.voice = ['feminine', 'masculine', 'neutral'].includes(voice) ? voice : 'neutral';
    if (manual) this.#profile.voiceManual = true;
    this.#saveProfile(this.#profile);
    if (this.dialog.open && this.#scr === 'results') this.#renderResults();
  }

  async #detectVoice(source) {
    if (this.#cfg.gender_detect === false || !this.#profile || this.#profile.voiceManual) return;
    try {
      if (!this.#faceapi) {
        if (!window.faceapi) await new Promise((res, rej) => { const sc = document.createElement('script'); sc.src = `${FA_BASE}/dist/face-api.min.js`; sc.onload = res; sc.onerror = rej; document.head.append(sc); });
        await Promise.all([window.faceapi.nets.tinyFaceDetector.loadFromUri(FA_WEIGHTS), window.faceapi.nets.ageGenderNet.loadFromUri(FA_WEIGHTS)]);
        this.#faceapi = window.faceapi;
      }
      const det = await this.#faceapi.detectSingleFace(source, new this.#faceapi.TinyFaceDetectorOptions({ inputSize: 320 })).withAgeAndGender();
      if (!det || this.#profile.voiceManual) return;
      const voice = det.genderProbability >= GENDER_MIN_CONFIDENCE ? (det.gender === 'female' ? 'feminine' : 'masculine') : 'neutral';
      this.#setVoice(voice, false);
    } catch (err) { console.warn('[virtual-try-on] tone detection skipped', err); }
  }

  /* ---------- STEP 2: verdict ---------- */
  #bestAlternative() {
    const m = this.#profile; let best = null;
    for (const p of this.#catalogue) {
      const rep = fitReport(m, p.specs, m.shape, this.#cfg.tolerance); if (!rep) continue;
      if (!best || rep.score > best.report.score) best = { ...p, report: rep };
    }
    return best;
  }

  /* ---------- screens ---------- */

  /** scan | results | details. The stage is shared, so the camera and the 3D renderer never restart. */
  #screen(name) {
    if (this.#scr === name) return;
    this.#scr = name;
    this.frame.dataset.screen = name;
    this.frame.scrollLeft = 0; this.frame.scrollTop = 0;
    try { $(this, 'detailsScreen').inert = name !== 'details'; } catch {}
    $(this, 'topLabel').textContent = name === 'scan' ? 'FACE SCAN' : '';
    if (name === 'results') this.#renderResults();
    if (name === 'details') this.#renderDetails();
    // the hero is a different shape from the full-bleed scan view, so the renderer needs re-measuring
    requestAnimationFrame(() => this.#resizeStage());
  }

  /** Full-face view: slide the results sheet away so the stage fills the frame; the pill above the CTA restores it. */
  #toggleSheet() {
    if (this.frame.dataset.collapsed === '1') delete this.frame.dataset.collapsed;
    else this.frame.dataset.collapsed = '1';
    requestAnimationFrame(() => this.#resizeStage());
    setTimeout(() => { if (this.dialog.open) this.#resizeStage(); }, 420); // again at the transition's end so the 3D canvas settles at the final size
  }

  #resizeStage() {
    const st = $(this, 'stage');
    const w = st.clientWidth, h = st.clientHeight;
    if (!w || !h) return;
    try { this.#tryon3d?.resize(w, h); } catch {}
    if (this.#mode === 'photo') this.#redrawStill();
    this.#layoutScan();
  }

  /** The design fixes the oval at top 150 on an 844px card; real phones vary, so centre it
   *  in the space that is actually visible between the top bar and the scan sheet. */
  #layoutScan() {
    if (this.#scr !== 'scan') return;
    const frameH = this.frame.clientHeight, sheetH = $(this, 'scanSheet').offsetHeight;
    if (!frameH || !sheetH) return;
    const topClear = 108, pill = 64; // the hint pill + its gap live BELOW the oval — shrink the oval before ever overlapping it
    const avail = frameH - sheetH - topClear;
    const ovalH = Math.round(Math.min(290, Math.max(190, avail - pill)));
    this.#ovalH = ovalH;
    const top = Math.round(Math.max(topClear, topClear + (avail - pill - ovalH) / 2));
    this.frame.style.setProperty('--ovalH', `${ovalH}px`);
    this.frame.style.setProperty('--ovalW', `${Math.round(ovalH * 220 / 290)}px`);
    this.frame.style.setProperty('--ovalTop', `${top}px`);
    this.frame.style.setProperty('--hintTop', `${top + ovalH + 18}px`);
  }

  /* ---------- 01 scan ---------- */

  /** Progress, hint and checklist. Driven by the real measuring loop, not a timer. */
  #scanUI(prog, msg) {
    const pct = Math.round(clamp(prog, 0, 1) * 100);
    if (pct !== this.#lastPct) {
      this.#lastPct = pct;
      $(this, 'pct').textContent = `${pct}%`;
      $(this, 'fill').style.transform = `scaleX(${prog})`;
      for (const row of $(this, 'checks').children) row.classList.toggle('is-on', pct >= Number(row.dataset.at));
      $(this, 'oval').classList.toggle('is-done', pct >= 99);
    }
    const hint = pct >= 99 ? 'Got it' : pct >= 25 ? (msg || 'Hold still…') : (msg || 'Centre your face in the oval');
    const el = $(this, 'hint');
    if (el.textContent !== hint) el.textContent = hint;
  }

  /** Scan line sweep. One rAF, transform only, eased so it eases at the turns. */
  #sweep = (t) => {
    if (this.#scr !== 'scan' || !this.dialog.open) { this.#sweepRaf = 0; return; }
    if (!this.#sweepT0) this.#sweepT0 = t;
    const u = ((t - this.#sweepT0) / 2200) % 1;           // 2.2 s down-and-back
    const e = u < 0.5 ? u * 2 : 2 - u * 2;                 // triangle
    const eased = e * e * (3 - 2 * e);                     // smoothstep
    $(this, 'line').style.transform = `translateY(${(this.#ovalH * 0.2 + eased * this.#ovalH * 0.6).toFixed(1)}px)`;
    this.#sweepRaf = requestAnimationFrame(this.#sweep);
  };

  #startSweep() { if (!this.#sweepRaf) { this.#sweepT0 = 0; $(this, 'line').style.opacity = '.8'; this.#sweepRaf = requestAnimationFrame(this.#sweep); } }
  #stopSweep() { if (this.#sweepRaf) cancelAnimationFrame(this.#sweepRaf); this.#sweepRaf = 0; $(this, 'line').style.opacity = '0'; }

  /* ---------- 02 results ---------- */

  /** Everything on the results sheet for whichever frame is current. */
  #renderResults() {
    const m = this.#profile;
    const f = this.#frameNow();
    if (!f) return;
    const rep = f.report;
    $(this, 'pName').textContent = f.title;
    $(this, 'pColor').textContent = f.colour || '';
    $(this, 'pPrice').textContent = f.priceStr || '';
    $(this, 'ctaPrice').textContent = f.priceStr || '';
    if (rep) {
      $(this, 'heroScore').textContent = rep.score;
      $(this, 'verdict').textContent = VERDICT(rep.score);
      $(this, 'ring').style.strokeDashoffset = String(RING_C * (1 - clamp(rep.score, 0, 100) / 100));
    } else {
      $(this, 'heroScore').textContent = '—';
      $(this, 'verdict').textContent = 'Here is how it looks.';
      $(this, 'ring').style.strokeDashoffset = String(RING_C);
    }
    $(this, 'chips').hidden = !!this.#other || this.#variants.length < 2;
    this.#renderTiles();
    this.#renderAlso();
    $(this, 'alsoWrap').hidden = !$(this, 'alsoRow').children.length;
  }

  #renderTiles() {
    const rep = this.#frameNow()?.report;
    const host = $(this, 'tiles');
    if (!rep) { host.innerHTML = ''; $(this, 'explain').textContent = 'No fit data for this frame yet.'; return; }
    host.innerHTML = METRICS.map((def, i) => {
      const pt = rep.points.find((x) => x.key === def.key) || {};
      return `<button type="button" class="pf__tile${i === this.#metric ? ' is-on' : ''}" data-action="metric" data-i="${i}" aria-pressed="${String(i === this.#metric)}">
        <svg viewBox="0 0 24 24" aria-hidden="true">${def.icon}</svg>
        <span class="pf__tk">${def.K}</span><span class="pf__tv">${esc(pt.short || '')}</span></button>`;
    }).join('');
    this.#renderExplain();
  }

  #renderExplain() {
    const rep = this.#frameNow()?.report; if (!rep) return;
    const def = METRICS[this.#metric];
    const pt = rep.points.find((x) => x.key === def.key) || {};
    $(this, 'explain').textContent = pt.text || '';
    $(this, 'marker').style.left = markerPos(pt.offset);
    $(this, 'lo').textContent = def.LO;
    $(this, 'hi').textContent = def.HI;
  }

  #pickMetric(i) {
    if (!Number.isFinite(i) || i === this.#metric) return;
    this.#metric = i;
    for (const b of $(this, 'tiles').children) {
      const on = Number(b.dataset.i) === i;
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on));
    }
    this.#renderExplain();
  }

  /** Alternatives, best first, current frame excluded. Tapping one makes it current. */
  #renderAlso() {
    const m = this.#profile, host = $(this, 'alsoRow');
    if (!m) { host.innerHTML = ''; return; }
    const here = this.#frameNow();
    const rows = [];
    for (const p of this.#catalogue) {
      if (here && p.url && here.url && p.url === here.url) continue;
      const rep = fitReport(m, p.specs, m.shape, this.#cfg.tolerance);
      if (rep) rows.push({ ...p, report: rep });
    }
    rows.sort((a, b) => b.report.score - a.report.score);
    this.#alts = rows.slice(0, 6);
    host.innerHTML = this.#alts.map((p, i) => `<button type="button" class="pf__altcard" data-action="alt" data-i="${i}" aria-label="${esc(p.title)}, scores ${p.report.score} out of 100">
      <span class="pf__altimg">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ''}<span class="pf__altscore">${p.report.score}</span></span>
      <span class="pf__altname">${esc(p.title)}</span><span class="pf__altprice">${esc(p.priceStr || '')}</span></button>`).join('');
  }

  /** Swap to an alternative without a page load: the old frame drops back into the list. */
  #pickAlt(i) {
    const p = this.#alts[i]; if (!p) return;
    this.#other = { title: p.title, colour: '', priceStr: p.priceStr || '', url: p.url, image: p.image, specs: p.specs, report: p.report, vid: p.vid };
    this.#metric = 0;
    if (p.tryonUrl) {
      const done = this.#tryon3d?.setFrame({ url: p.tryonUrl, modelUrl: p.tryonModel || '', widthMm: p.specs?.width, templeMm: p.specs?.temple, faceWidthMm: this.#profile?.faceW });
      if (done?.then) done.then((ok) => ok && this.#redrawStill());
    }
    this.#renderResults();
    $(this, 'sheet').scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** The frame the screen is describing: an alternative once tapped, otherwise this product. */
  #frameNow() {
    if (this.#other) return this.#other;
    const m = this.#profile;
    return {
      title: this.#product.title || '',
      colour: this.#current?.title && this.#current.title !== 'Default Title' ? this.#current.title : '',
      priceStr: this.#current?.price || this.#product.price || '',
      url: this.#product.url || '',
      specs: this.#specs,
      report: m ? fitReport(m, this.#specs, m.shape, this.#cfg.tolerance) : null,
    };
  }

  /* ---------- 03 details ---------- */

  #renderDetails() {
    const m = this.#profile, f = this.#frameNow(), rep = f?.report;
    if (!m || !rep) return;
    $(this, 'dTitle').textContent = `Why this frame scores ${rep.score}`;
    $(this, 'dSub').textContent = `${f.title} compared to your measurements.`;
    $(this, 'meas').innerHTML = MEASURE_KEYS.map(([K, key, unit]) => {
      const v = key === 'shape' ? (SHAPE_LABEL[m.shape] || m.shape) : `${r0(key === 'noseW' ? m.noseW * 0.6 : m[key])}${unit}`;
      return `<div class="pf__mtile"><span class="pf__mk">${K}</span><span class="pf__mv">${esc(String(v))}</span></div>`;
    }).join('');
    $(this, 'dRows').innerHTML = METRICS.map((def) => {
      const pt = rep.points.find((x) => x.key === def.key) || {};
      return `<div class="pf__drow">
        <div class="pf__drowtop"><span class="pf__dname">${esc(pt.label || def.K)}</span><span class="pf__dpts">${pt.pts} / ${pt.weight} pts</span></div>
        <div class="pf__bar"><div class="pf__ideal"></div><div class="pf__marker" style="left:${markerPos(pt.offset)}"></div></div>
        <span class="pf__dtext">${esc(pt.text || '')}</span></div>`;
    }).join('');
  }

  /* ---------- photo path ---------- */
  #renderPhoto() {
    const c = this.#photo; if (!c) return;
    this.canvas.width = c.width; this.canvas.height = c.height;
    const res = this.#photoRes || (this.#photoRes = this.#landmarker.detect(c)), lm = res.faceLandmarks?.[0];
    this.#smooth = null; this.#draw(c, res, false);
    // object-fit: cover would crop a tall photo to its middle; keep the eye line in view
    this.#setObjectPosition(lm ? `50% ${clamp(Math.round(((lm[33].y + lm[263].y) / 2) * 100), 0, 100)}%` : '50% 50%');
    this.#setStatus(lm ? '' : 'No face found — try a straight-on photo.');
  }

  #draw(source, res, mirror) {
    const { ctx, canvas } = this; const W = canvas.width, H = canvas.height;
    const lm = res?.faceLandmarks?.[0], t3 = this.#tryon3d;
    // 3D path: only the video/photo goes on the 2D canvas, the glasses render in the WebGL layer above it.
    // A throwing renderer must never blank the video, so it demotes itself to the flat overlay.
    this.#use3d = false;
    if (t3) {
      try { t3.resize(W, H); this.#use3d = t3.update(res, { mirror, instant: this.#mode !== 'video' }); }
      catch (err) { this.#fail('3D renderer', err); this.#tryon3d = null; this.#threeState = 'failed'; this.#tryon3dReady = Promise.resolve(null); try { t3.dispose(); } catch {} }
    }
    ctx.save();
    if (mirror) { ctx.translate(W, 0); ctx.scale(-1, 1); }
    ctx.drawImage(source, 0, 0, W, H);
    if (lm && this.#image && !this.#use3d && this.#threeState !== 'loading') {
      const P = (i) => ({ x: lm[i].x * W, y: lm[i].y * H });
      const eL = P(LM.eyeL), eR = P(LM.eyeR), fL = P(LM.faceL), fR = P(LM.faceR), nose = P(LM.nose);
      const target = { cx: (eL.x + eR.x) / 2, cy: (eL.y + eR.y) / 2, angle: Math.atan2(eR.y - eL.y, eR.x - eL.x), width: dist(fL, fR) * 1.06, yaw: (nose.x - (eL.x + eR.x) / 2) / Math.max(1, dist(eL, eR)) };
      const s = this.#smooth;
      this.#smooth = s ? { cx: lerp(s.cx, target.cx, 0.45), cy: lerp(s.cy, target.cy, 0.45), angle: lerp(s.angle, target.angle, 0.4), width: lerp(s.width, target.width, 0.3), yaw: lerp(s.yaw, target.yaw, 0.3) } : target;
      const f = this.#smooth, img = this.#image;
      // true-to-size: scale the overlay by the frame's real width against the measured face width when both are known
      const ratio = this.#specs?.width && this.#profile?.faceW ? clamp(this.#specs.width / this.#profile.faceW, 0.8, 1.25) : 1;
      const w = f.width * ratio, h = w * (img.naturalHeight / img.naturalWidth);
      const squash = Math.max(0.55, 1 - Math.abs(f.yaw) * 1.1);
      ctx.translate(f.cx + f.yaw * w * 0.12, f.cy + h * 0.06); ctx.rotate(f.angle); ctx.scale(squash, 1);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
    }
    if (this.#mode === 'video' && !this.#scanning && this.#lmMode === 'VIDEO' && performance.now() > this.#noticeUntil) this.#setStatus(lm && this.#image ? '' : 'Centre your face in the frame');
    ctx.restore();
  }
  #setObjectPosition(pos) { this.canvas.style.objectPosition = pos; const c3 = this.#tryon3d?.canvas; if (c3) c3.style.objectPosition = pos; }
  /** Re-render the photo after a variant / fit / 3D change (the live loop picks those up by itself). */
  #redrawStill() { if (this.dialog.open && this.#mode === 'photo') this.#renderPhoto(); }
  /** Lazy 3D renderer. Resolves null (and logs once) when WebGL / three / the occluder are unavailable → flat overlay. */
  #ensure3d() {
    if (this.#tryon3dReady) return this.#tryon3dReady;
    const url = this.getAttribute('data-asset-3d'), occ = this.getAttribute('data-asset-occluder'); // dataset does not camel-case "asset-3d"
    this.#threeState = 'loading';
    this.#tryon3dReady = (async () => {
      if (!url || !occ) throw new Error('3D assets not configured');
      const { createTryOn3D } = await import(url);
      this.#tryon3d = await createTryOn3D({ host: $(this, 'stage3d'), occluderUrl: occ });
      if (Object.keys(this.#tune).length) this.#tryon3d.setTune(this.#tune);
      this.#threeState = 'ready';
      this.#sync3dFrame();
      return this.#tryon3d;
    })().catch((err) => { this.#threeState = 'failed'; this.#fail('3D renderer (using the flat overlay)', err); return null; });
    return this.#tryon3dReady;
  }
  #sync3dFrame() {
    const v = this.#current; if (!this.#tryon3d || !v) return;
    this.#tryon3d.setFrame({ url: v.url, modelUrl: v.model || '', widthMm: this.#specs?.width, templeMm: this.#specs?.temple, faceWidthMm: this.#profile?.faceW }).then((ok) => ok && this.#redrawStill());
  }

  /* ---------- checkout / variants / misc ---------- */
  #ctaLabel() {
    const tpl = this.#cfg.express_checkout === false ? 'Add to bag · {price}' : this.#cfg.cta_label;
    return fill(tpl, { price: this.#product.price || '' }).replace(/\s*·\s*$/, '');
  }
  #checkout() {
    if (this.#other?.vid) {
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.#other.vid, quantity: 1 }) })
        .then((r) => { if (!r.ok) throw new Error('add failed'); window.location.href = '/checkout'; })
        .catch(() => { window.location.href = this.#other.url || '/cart'; });
      return;
    }
    const id = this.#current?.id || this.#currentVariantId();
    if (!id) return;
    if (this.#cfg.express_checkout === false) {
      // plain add to bag: hand off to the theme's own product form (keeps its cart drawer behaviour)
      const section = this.closest('.shopify-section') || document;
      const input = section.querySelector('input[name="id"]'); if (input) input.value = id;
      const btn = section.querySelector('.add-to-cart-button, button[name="add"]');
      this.close(); btn?.click();
      return;
    }
    window.location.href = `/cart/${id}:1`; // Shopify cart permalink → straight to checkout
  }
  #currentVariantId() {
    const input = (this.closest('.shopify-section') || document).querySelector('input[name="id"]');
    return Number(input?.value || this.dataset.selected);
  }
  #buildChips() {
    const chips = $(this, 'chips'); chips.replaceChildren();
    for (const v of this.#variants) {
      const b = document.createElement('button'); b.type = 'button';
      const label = v.title === 'Default Title' ? 'Original' : v.title;
      // the store's own option swatch (what the product page shows) wins; the title-guess palette is the fallback
      const sw = v.swatch;
      const disc = sw
        ? `<i class="pf__disc" style="${sw.image ? `background:url('${sw.image}') center/cover` : `background:${sw.color}`}"></i>`
        : swatchDisc(v.title);
      b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', 'false'); b.dataset.id = v.id;
      b.setAttribute('aria-label', label); b.title = label;
      if (disc) { b.className = 'pf__chip'; b.innerHTML = disc; }
      else { b.className = 'pf__chip pf__chip--text'; b.textContent = label; }
      b.addEventListener('click', () => { this.#other = null; this.#select(v.id); if (this.#scr === 'results') this.#renderResults(); });
      chips.append(b);
    }
  }

  #select(id) {
    const v = this.#variants.find((x) => x.id === Number(id)) || this.#variants[0];
    if (!v) return;
    this.#current = v; this.#sync3dFrame();
    for (const chip of $(this, 'chips').children) chip.setAttribute('aria-checked', String(Number(chip.dataset.id) === v.id));
    const img = new Image(); img.crossOrigin = 'anonymous'; img.src = v.url;
    img.decode().then(() => { this.#image = img; this.#redrawStill(); }).catch(() => {});
  }
  /** Notices ride in the scan hint pill; a data flag lets one show over the results hero too. */
  #setStatus(text) {
    const el = $(this, 'hint');
    if (el.textContent !== text) el.textContent = text;
    el.parentElement.dataset.notice = text ? '1' : '';
  }
  /** Download the current try-on view as a JPEG. */
  #stopStream() { cancelAnimationFrame(this.#raf); this.#stream?.getTracks().forEach((t) => t.stop()); this.#stream = null; this.video.srcObject = null; }
  #teardown() { this.#gen++; document.documentElement.classList.remove('pl-tryon-open'); this.#stopStream(); this.#mode = 'idle'; this.#photo = null; this.#photoRes = null; this.#prevScan = null; this.#scanning = false; this.frame.dataset.scanning = ''; this.#noticeUntil = 0; this.#smooth = null; this.#samples = []; this.#lastRes = null; this.#use3d = false; this.#stopSweep(); this.#other = null; this.#tryon3d?.clear(); this.#setObjectPosition('50% 50%'); this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height); }
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('virtual-try-on')) customElements.define('virtual-try-on', VirtualTryOn);
